import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { fmtTime } from "../../lib/time";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Save, Loader2, Lock, CheckCircle2 } from "lucide-react";

export default function PromoterStock() {
  const [skus, setSkus] = useState([]);
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(true);
  const [existing, setExisting] = useState(null); // existing locked stock entry
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [s, me] = await Promise.all([api.get("/promoter/skus"), api.get("/promoter/me")]);
        setSkus(s.data);
        setActive(!!me.data.current_shift);
        try {
          const c = await api.get("/promoter/stock/current");
          if (c.data) setExisting(c.data);
        } catch {}
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const change = (id, v) => {
    if (v === "") {
      setQty((q) => {
        const next = { ...q };
        delete next[id];
        return next;
      });
      return;
    }
    const n = parseInt(v, 10);
    setQty((q) => ({ ...q, [id]: isNaN(n) ? 0 : Math.max(0, n) }));
  };

  const save = async () => {
    // Validate ALL SKUs have a value
    const missing = skus.filter((s) => qty[s.id] === undefined || qty[s.id] === null);
    if (missing.length) {
      toast.error(
        `Please enter quantity for: ${missing.map((m) => m.name).join(", ")}`,
      );
      return;
    }
    setBusy(true);
    try {
      const quantities = {};
      for (const s of skus) quantities[s.id] = qty[s.id] || 0;
      const { data } = await api.post("/promoter/stock", { quantities });
      setExisting(data);
      toast.success("Stock recorded · locked for this shift");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-neutral-500 text-sm">Loading…</div>;

  if (!active) {
    return (
      <div className="glass-panel p-6 text-center text-sm text-neutral-500">
        Check in first to record stock.
      </div>
    );
  }

  // Locked / read-only view
  if (existing) {
    const initial = existing.initial_quantities || existing.quantities;
    return (
      <div className="space-y-4" data-testid="stock-locked-view">
        <div>
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            <Lock className="h-3 w-3" /> Locked for this shift
          </div>
          <h1 className="font-display font-semibold text-3xl tracking-tight">Stock count</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Recorded at{" "}
            <span className="font-mono">
              {fmtTime(existing.created_at)}
            </span>
            . Available stock will decrease automatically as you log sales.
          </p>
        </div>

        <div className="glass-panel divide-y divide-slate-200/50">
          <div className="grid grid-cols-3 gap-3 bg-white/50 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            <div>SKU</div>
            <div className="text-right">Initial</div>
            <div className="text-right">Available</div>
          </div>
          {skus.map((s) => {
            const init = initial?.[s.id] ?? 0;
            const cur = existing.quantities?.[s.id] ?? 0;
            const sold = init - cur;
            return (
              <div key={s.id} className="grid grid-cols-3 gap-3 px-4 py-3 items-center">
                <div className="text-sm font-medium">{s.name}</div>
                <div className="text-right font-mono text-sm text-neutral-500">{init}</div>
                <div className="text-right">
                  <span
                    className={`font-mono font-semibold ${
                      cur === 0 ? "text-red-600" : "text-black"
                    }`}
                  >
                    {cur}
                  </span>
                  {sold > 0 && (
                    <span className="ml-2 text-[10px] font-mono text-emerald-600 uppercase tracking-widest">
                      −{sold} sold
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-start gap-2 glass-panel bg-white/50 p-3 text-xs text-neutral-600">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
          <div>
            Stock count is final for this shift. In case of amendment, contact Promotify.
          </div>
        </div>
      </div>
    );
  }

  // Initial entry form
  return (
    <div className="space-y-4">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
          Stock count · enter once
        </div>
        <h1 className="font-display font-semibold text-3xl tracking-tight">Available SKUs</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Enter the quantity available at the shelf for <strong>every</strong> SKU. This will be
          locked once saved.
        </p>
      </div>

      <div className="glass-panel divide-y divide-slate-200/60" data-testid="stock-list">
        {skus.map((s) => {
          const missing = qty[s.id] === undefined || qty[s.id] === null;
          return (
            <div
              key={s.id}
              className={`flex items-center justify-between gap-3 p-4 ${
                missing ? "bg-amber-50/40" : ""
              }`}
            >
              <div className="flex-1">
                <div className="font-medium" data-testid={`stock-sku-${s.id}`}>
                  {s.name}
                </div>
                {missing && (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mt-0.5">
                    Required
                  </div>
                )}
              </div>
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                value={qty[s.id] ?? ""}
                placeholder="0"
                onChange={(e) => change(s.id, e.target.value)}
                className={`rounded-xl w-24 text-right font-mono ${
                  missing ? "border-amber-400" : ""
                }`}
                data-testid={`stock-qty-${s.id}`}
              />
            </div>
          );
        })}
      </div>

      <Button
        onClick={save}
        disabled={busy}
        className="rounded-xl w-full h-14 bg-slate-900 hover:bg-slate-700 text-white uppercase tracking-wider font-semibold"
        data-testid="save-stock-button"
      >
        {busy ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            <Save className="h-5 w-5 mr-2" /> Save stock (locked after save)
          </>
        )}
      </Button>
    </div>
  );
}
